import { Component, HostListener } from '@angular/core';
import { ModulesService } from '../../services/modules.service';
import { firstValueFrom } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { User, UserTier } from '../../models/User';
import { Level, ScoresData } from '../../models/ScoresResponse';
import { WordService } from '../../services/word.service';
import { Module } from '../../models/Module';
import { CommonModule } from '@angular/common';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Router, RouterModule } from '@angular/router';
import { ModalService } from '../../services/modal.service';
import { DiplomaModalComponent } from '../modales/diploma-modal/diploma-modal.component';
import { Role } from '../../models/Role';
import { PaymentModalComponent } from '../modales/payment-modal/payment-modal.component';
import { ModuleAccess } from '../../models/ModuleAccess';
import { OfferBannerComponent } from '../offer-banner/offer-banner.component';
import { LottieComponent } from 'ngx-lottie';
import { AnimationOptions } from 'ngx-lottie';
import { PremiumOfferService } from '../../services/premium-offer.service';

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterModule, NgbCollapseModule, OfferBannerComponent, TranslateModule, LottieComponent],
  templateUrl: './home-child.component.html',
  styleUrl: './home-child.component.scss',
})
export class HomeChildComponent {
  user?: User;
  scores?: ScoresData;
  modules?: Module[];
  levels: Level[] = [Level.bronze, Level.argent, Level.or, Level.diamant];
  moduleAccess: ModuleAccess[] = [];
  shouldDisplayPremiumOffer: boolean = false;
  isGarUser: boolean = false;
  isProfilePanelOpen = false;

  avatarAnimationOptions: AnimationOptions = {
    loop: true,
    autoplay: true,
  };
  private medalAnimationOptions = new Map<string, AnimationOptions>();

  constructor(
    private router: Router,
    private toaster: ToastrService,
    private authService: AuthService,
    private moduleService: ModulesService,
    private wordService: WordService,
    private modalService: ModalService,
    private translationService: TranslateService,
    private premiumOfferService: PremiumOfferService,
  ) {}

  async ngOnInit() {
    try {
      const user = await firstValueFrom(this.authService.getUserInfoOrRefreshIfNeeded());
      const scores = await firstValueFrom(this.authService.getMyScores(Role.enfant));
      const modules = await firstValueFrom(this.moduleService.getModules(Role.enfant));
      const moduleAccess: ModuleAccess[] = await firstValueFrom(this.moduleService.getModulesAccess());

      this.user = user;
      this.isGarUser = Boolean(user.gar_ido);
      this.moduleAccess = moduleAccess;
      this.scores = scores.data[0];
      this.modules = modules;
      this.shouldDisplayPremiumOffer = this.premiumOfferService.canSeePremiumOffers(user);

      this.avatarAnimationOptions = {
        ...this.avatarAnimationOptions,
        path: `assets/lottie/avatars/${this.user.avatar_id ?? 1}.json`,
      };

      this.redirectToSurveyIfNeeded();
    } catch {
      this.toaster.error(this.translationService.instant('common.labels.error'));
    }
  }

  isTP(): boolean {
    if (this.user === undefined) {
      return false;
    }

    return this.user.is_parent || this.user.is_teacher;
  }

  redirectToSurveyIfNeeded() {
    const surveyDone = this.user?.is_survey_finished;
    const allModulesDone = this.areAllModulesDone();

    if (allModulesDone && !surveyDone) {
      this.router.navigate(['/survey']);
    }
  }

  currentLevel(): string {
    let res = '';

    if (this.scores?.global_level) {
      return this.translationService.instant(this.scores.global_level);
    }

    return this.wordService.capitalizeFirstLetter(res);
  }

  nextLevel(): string {
    let res = '';

    if (this.scores?.global_next_level) {
      return this.translationService.instant(this.scores.global_next_level);
    }

    return this.wordService.capitalizeFirstLetter(res);
  }

  evenModules(): Module[] {
    if (!this.modules) {
      return [];
    }

    return this.modules.filter((item, index) => index % 2 === 0);
  }

  oddModules(): Module[] {
    if (!this.modules) {
      return [];
    }

    return this.modules.filter((item, index) => index % 2 !== 0);
  }

  progressStyleFor(module: Module): any {
    let obj: any = {
      width: `${this.progressFor(module)}%`,
    };

    return obj;
  }

  progressFor(module: Module): number {
    if (!this.scores) {
      return 0;
    }

    let progress = this.scores.progresses.find((elt) => elt.module_id === module.module_enum);

    if (!progress) {
      return 0;
    }

    let percentage = 0;

    if (progress.validated_leagues.includes(Level.bronze)) {
      percentage = 25;
    }
    if (progress.validated_leagues.includes(Level.argent)) {
      percentage = 50;
    }
    if (progress.validated_leagues.includes(Level.or)) {
      percentage = 75;
    }
    if (progress.validated_leagues.includes(Level.diamant)) {
      percentage = 100;
    }

    return percentage;
  }

  getBadge(level: Level, module: Module) {
    const levelPath: string = level;
    let progressPath = 'notdone';

    if (this.scores) {
      let progress = this.scores.progresses.find((elt) => elt.module_id === module.module_enum);

      if (progress) {
        if (progress.validated_leagues.includes(level)) {
          progressPath = 'done';
        }
      }
    }

    let path = `assets/levels/${progressPath}/${levelPath}.png`;

    return path;
  }

  getBackgroundColorForCard(module: Module): any {
    let obj: any = {
      'background-color': this.moduleService.getModuleBgColorName(module, undefined),
    };

    return obj;
  }

  getColorForCard(module: Module): any {
    let obj: any = {
      color: this.moduleService.getModuleBgColorName(module, undefined),
    };

    return obj;
  }

  getModuleImg(module: Module): string {
    return module.illustration_url;
  }

  async playModule(module: Module, event: MouseEvent) {
    await this.checkFromUserTierBeforeNavigation(module);
  }

  shouldDisplayMedal(module: Module): boolean {
    return this.getMedalString(module) !== 'bois';
  }

  medalAnimationOptionsForModule(module: Module): AnimationOptions {
    const moduleId = module.module_enum;

    // Check if we already have animation options for this module
    if (this.medalAnimationOptions.has(moduleId)) {
      return this.medalAnimationOptions.get(moduleId)!;
    }

    const medalPath = this.getMedalString(module);
    const animationPath = `assets/lottie/levels/${medalPath}.json`;

    const options: AnimationOptions = {
      path: animationPath,
      autoplay: true,
      loop: false,
    };

    this.medalAnimationOptions.set(moduleId, options);

    return options;
  }

  onMedalAnimationCreated(animationItem: any, module: Module): void {
    animationItem.addEventListener('complete', () => {
      // Generate random delay between 2 and 10 seconds (in milliseconds)
      const randomDelay = Math.floor(Math.random() * 8000) + 2000;

      setTimeout(() => {
        animationItem.goToAndPlay(0);
      }, randomDelay);
    });
  }

  onError(event: any) {
    console.error('Lottie error:', event);
  }

  private getMedalString(module: Module): string {
    let medalPath = 'bois';

    if (this.scores) {
      let progress = this.scores.progresses.find((elt) => elt.module_id === module.module_enum);

      if (progress) {
        if (progress.validated_leagues.includes(Level.diamant)) {
          medalPath = 'diamant';
        } else if (progress.validated_leagues.includes(Level.or)) {
          medalPath = 'or';
        } else if (progress.validated_leagues.includes(Level.argent)) {
          medalPath = 'argent';
        } else if (progress.validated_leagues.includes(Level.bronze)) {
          medalPath = 'bronze';
        }
      }
    }

    return medalPath;
  }

  areAllModulesDone(): boolean {
    if (this.scores === undefined) {
      return false;
    }

    return this.scores.progresses.length === this.scores.progresses.filter((elt) => elt.validated_leagues.includes(Level.diamant)).length;
  }

  shouldDisplayDays(): boolean {
    return this.user?.nb_days_left !== undefined && this.user.nb_days_left !== null;
  }

  shouldDisplayDiploma(): boolean {
    return this.areAllModulesDone();
  }

  openProfilePanel(): void {
    this.isProfilePanelOpen = true;
  }

  closeProfilePanel(): void {
    this.isProfilePanelOpen = false;
  }

  @HostListener('document:keydown.escape')
  closeProfilePanelOnEscape(): void {
    if (this.isProfilePanelOpen) {
      this.closeProfilePanel();
    }
  }

  profileDisplayName(): string {
    const fullName = [this.user?.first_name, this.user?.last_name].filter(Boolean).join(' ').trim();

    return fullName || this.user?.pseudo || this.user?.email || '';
  }

  accountRoleKey(): string {
    if (this.user?.is_teacher) {
      return 'roles.teacher';
    }

    if (this.user?.is_parent) {
      return 'roles.parent';
    }

    return 'roles.student';
  }

  profileProgressPercentage(): number {
    const score = this.scores?.global_score ?? 0;
    const maximum = this.scores?.global_max_score ?? 0;

    if (maximum <= 0) {
      return 0;
    }

    return Math.min(100, Math.max(0, (score / maximum) * 100));
  }

  openDiplomaModale() {
    this.closeProfilePanel();
    const graphism = this.user?.graphism ?? 'manga';
    const modalRef = this.modalService.open(DiplomaModalComponent, {
      size: 'lg',
    });
    modalRef.componentInstance.graphism = graphism;
    modalRef.result.then(() => {});
  }

  checkFromUserTierBeforeNavigation(module: Module) {
    if (this.isContentLocked(module)) {
      this.openPaymentModal();
    } else {
      this.router.navigate(['/module_start', module.module_enum]);
    }
  }

  openPaymentModal() {
    const modalRef = this.modalService.open(PaymentModalComponent, {
      size: 'lg',
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.result.then((result) => {
      if (result === 'cancel') {
        // ici
      }
    });
  }

  shouldDisplayPlayButton(module: Module): boolean {
    const moduleEnum = module.module_enum;
    const moduleScore = this.scores!.progresses.find((elt) => elt.module_id === moduleEnum);
    const nextLevel = moduleScore?.next_level;

    return nextLevel !== null;
  }

  isContentLocked(module: Module): boolean {
    const moduleEnum = module.module_enum;
    const moduleScore = this.scores!.progresses.find((elt) => elt.module_id === moduleEnum);
    const nextLevel = moduleScore?.next_level;

    if (!nextLevel) {
      return false;
    }

    const tier = this.user?.tier;

    if (tier === UserTier.premium) {
      return false;
    }

    const access = this.moduleAccess.find((elt) => elt.module_enum === moduleEnum && elt.level === nextLevel);

    if (!access) {
      return true;
    }

    return access.tier === UserTier.premium;
  }

  getBackgroundColorForAvatar(): string {
    let avatar: number = 0;
    if (this.user?.avatar_id !== undefined) {
      avatar = parseInt(this.user.avatar_id) - 1;
    }
    const array = ['#5a36b2', '#007eff', '#3abcd8', '#ec407a', '#0fc461', '#ee8b60', '#f4b60b'];

    return array[avatar % array.length];
  }
}
